import { useLocation, useSearchParams } from 'react-router-dom';
import { isAndroid, isIPad } from '@/lib/platform';
import { useAndroidBottomNavOffset } from '@/hooks/useAndroidBottomNavOffset';
import { useUserData } from '@/hooks/useUserData';
import { UsersBottomNav } from './UsersBottomNav';
import { ArtistsBottomNav } from './ArtistsBottomNav';
import { BookingSystemBottomNav } from './BookingSystemBottomNav';
import { BookingSystemDashboardBottomNav } from './BookingSystemDashboardBottomNav';

export function BottomNavWrapper() {
  const location = useLocation();
  const [navSearchParams] = useSearchParams();
  
  // Enhanced useUserData now provides everything we need in one hook!
  const { 
    isArtist, 
    artistId, 
    loading: userDataLoading,
    hasBookingSystem, 
    bookingSubscription,
    getCachedProfile 
  } = useUserData();
  
  // Get cached profile for instant UI on app launch (prevents flicker)
  const cachedProfile = getCachedProfile();
  
  // While Firestore is loading, fall back to same-user cache; once loaded, trust live data only.
  const effectiveIsArtist = userDataLoading
    ? (cachedProfile?.isArtist ?? isArtist)
    : isArtist;
  const effectiveHasBookingSystem = hasBookingSystem !== undefined && hasBookingSystem !== false ? hasBookingSystem : cachedProfile?.hasBookingSystem ?? false;
  const effectiveSubscriptionStatus = bookingSubscription?.status ?? cachedProfile?.bookingStatus ?? null;
  
  // Log cache hit for debugging
  if (cachedProfile) {
    console.log('[BottomNav] Using cached profile on page load:', {
      isArtist: effectiveIsArtist,
      hasBookingSystem: effectiveHasBookingSystem,
      firstName: cachedProfile.firstName
    })
  }

  // List of paths where we don't want to show the bottom nav
  const hiddenPaths = [
    '/privacy-policy',
    '/cookie-policy',
    '/artist-signup',
    '/onboarding',
    '/onboarding-admin',
    '/live-chat-admin',
    '/fotw-uploads',
    '/board-post-drafts',
    '/chats/new',
    '/booking-system/settings',
    '/booking-system/clients',
    '/giftcard',
    '/giftcard/success',
    '/events/add',
    '/events/add/',
    '/deals/add',
    '/deals/add/',
    '/healed/',
    '/booking',
    '/book',
    '/for-artists/book',
    '/live-chat',
    '/board/new',
    '/board/posts/',
  ];

  // Check if on /artist/:artistId path (including own profile — no bottom nav on artist pages)
  const isArtistDetailPath = location.pathname.startsWith('/artist/');

  // Check if the path looks like a dynamic artist name (single word path that's not one of our routes)
  // This is for the catch-all /:artistName route
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isDynamicArtistPath = pathSegments.length === 1 && 
    !['discover', 'feed', 'board', 'pulse', 'artists', 'events', 'swipe', 'chat', 'chats', 'admin', 'profile', 'settings', 'artist', 'booking-system', 'guest-artists', 'fotw', 'giftcard', 'rate-my-artist', 'live-chat', 'tattoo-artist-matcher', 'for-artists', 'city-requests'].includes(pathSegments[0]);

  // Check if we're on an event edit page (/events/:eventId/edit)
  const isEventEditPath = location.pathname.match(/^\/events\/[^/]+\/edit\/?$/);

  // Check if we're on a deals edit page (/deals/:dealId/edit)
  const isDealEditPath = location.pathname.match(/^\/deals\/[^/]+\/edit\/?$/);

  // Check if we should hide the bottom nav
  const shouldHideNav = 
    // Check regular hidden paths
    hiddenPaths.some(path => location.pathname === path || location.pathname.startsWith(`${path}/`)) ||
    // Check event edit path
    !!isEventEditPath ||
    // Check deal edit path
    !!isDealEditPath ||
    // Special case for chat detail pages: hide on /chats/* but not on /chats itself
    (location.pathname.startsWith('/chats/') && location.pathname !== '/chats') ||
    // Hide on all /artist/* profile pages (own or others)
    isArtistDetailPath ||
    // Hide on dynamic artist slug pages (own or others)
    isDynamicArtistPath ||
    // Hide when inside an active matcher step (pairwise, preferences, results) on mobile
    (location.pathname === '/tattoo-artist-matcher' && navSearchParams.get('matcherActive') === 'true');

  // Must run on every render — do not place after conditional returns.
  const androidNative = isAndroid();
  const androidBottomPx = useAndroidBottomNavOffset();

  // Don't render anything if we're on a hidden path
  if (shouldHideNav) {
    console.log('[BottomNav] Hidden on path:', location.pathname);
    return null;
  }

  // Check if we're in the booking system routes (including nested pages)
  const isInBookingSystem = location.pathname.startsWith('/booking-system/');
  
  // Check if user is on trial status (use effective value for instant UI)
  const isTrialStatus = effectiveSubscriptionStatus === 'trial';

  // Determine which nav to show based on user type and booking system access
  let NavComponent: React.ComponentType;
  const accessReason = !effectiveIsArtist ? 'not-artist' : (effectiveSubscriptionStatus ? `status-${effectiveSubscriptionStatus}` : 'no-subscription');
  console.log(`[BottomNav] Booking access: ${effectiveHasBookingSystem ? 'granted' : 'denied'} (${accessReason}) for artistId=${artistId ?? 'unknown'}`);
  if (effectiveIsArtist) {
    if (effectiveHasBookingSystem) {
      if (isInBookingSystem) {
        // Always show dashboard nav when in booking system routes (even for trial)
        console.log('[BottomNav] Showing BookingSystemDashboardBottomNav on path:', location.pathname);
        NavComponent = BookingSystemDashboardBottomNav;
      } else if (isTrialStatus) {
        // Trial users see regular artist nav outside of booking system
        console.log('[BottomNav] Showing ArtistsBottomNav for trial user on path:', location.pathname);
        NavComponent = ArtistsBottomNav;
      } else {
        console.log('[BottomNav] Showing BookingSystemBottomNav on path:', location.pathname);
        NavComponent = BookingSystemBottomNav;
      }
    } else {
      console.log('[BottomNav] Showing ArtistsBottomNav on path:', location.pathname);
      NavComponent = ArtistsBottomNav;
    }
  } else {
    NavComponent = UsersBottomNav;
  }

  // For booking system pages, always show the bottom nav (even on wide viewports)
  // For other pages, hide on medium screens and larger (iPad uses bottom nav like phone)
  const isStandaloneInfoPage =
    location.pathname === '/for-artists' || location.pathname === '/city-requests'
  const navVisibilityClass =
    isInBookingSystem || isStandaloneInfoPage || isIPad() ? '' : 'md:hidden';

  return (
    <div
      className={`${navVisibilityClass} bottom-nav-fixed fixed left-0 right-0 z-40 pointer-events-none${
        androidNative ? ' bottom-nav-fixed-android' : ''
      }`}
      style={{
        ...(androidNative
          ? { bottom: `${androidBottomPx}px`, paddingBottom: 0 }
          : {
              bottom: 0,
              paddingBottom:
                'calc(var(--bottom-nav-lift) + var(--bottom-nav-bottom-gutter))',
            }),
        paddingLeft: 'var(--min-side-padding)',
        paddingRight: 'var(--min-side-padding)',
      }}
    >
      <div className="pointer-events-auto">
        <div className="bottom-nav-shell rounded-3xl">
          <NavComponent />
        </div>
      </div>
    </div>
  );
}
